import { useEffect, useState } from 'react';

/**
 * Renders a logo with flat background (e.g. cyan) removed via canvas.
 * Logo artwork itself is unchanged — only near-uniform bg pixels become transparent.
 */
export default function TransparentLogo({
  src,
  alt = 'NAYAVARNAM',
  className = '',
  asMotion = false,
  MotionComponent,
  motionProps = {},
}) {
  const [cleanSrc, setCleanSrc] = useState('');

  useEffect(() => {
    if (!src) {
      setCleanSrc('');
      return undefined;
    }

    let cancelled = false;
    const img = new Image();
    // Only needed for remote CDN / Supabase URLs
    if (/^https?:\/\//i.test(src)) {
      img.crossOrigin = 'anonymous';
    }

    img.onload = () => {
      try {
        const canvas = document.createElement('canvas');
        canvas.width = img.naturalWidth || img.width;
        canvas.height = img.naturalHeight || img.height;
        const ctx = canvas.getContext('2d', { willReadFrequently: true });
        ctx.drawImage(img, 0, 0);
        const { data, width, height } = ctx.getImageData(0, 0, canvas.width, canvas.height);

        // Sample corners to detect background colour
        const corners = [
          0,
          (width - 1) * 4,
          (height - 1) * width * 4,
          ((height - 1) * width + (width - 1)) * 4,
        ];
        let br = 0;
        let bg = 0;
        let bb = 0;
        corners.forEach((i) => {
          br += data[i];
          bg += data[i + 1];
          bb += data[i + 2];
        });
        br /= 4;
        bg /= 4;
        bb /= 4;

        const threshold = 48;
        for (let i = 0; i < data.length; i += 4) {
          const dr = Math.abs(data[i] - br);
          const dg = Math.abs(data[i + 1] - bg);
          const db = Math.abs(data[i + 2] - bb);
          if (dr < threshold && dg < threshold && db < threshold) {
            data[i + 3] = 0;
          }
        }

        ctx.putImageData(new ImageData(data, width, height), 0, 0);
        const out = canvas.toDataURL('image/png');
        if (!cancelled) setCleanSrc(out);
      } catch {
        if (!cancelled) setCleanSrc(src);
      }
    };

    img.onerror = () => {
      if (!cancelled) setCleanSrc(src);
    };

    img.src = src;

    return () => {
      cancelled = true;
    };
  }, [src]);

  if (!cleanSrc) {
    return <span className={`${className} transparent-logo--loading`} aria-hidden />;
  }

  if (asMotion && MotionComponent) {
    return (
      <MotionComponent
        src={cleanSrc}
        alt={alt}
        className={className}
        {...motionProps}
      />
    );
  }

  return <img src={cleanSrc} alt={alt} className={className} />;
}
